import { joiResolver } from "@hookform/resolvers/joi";
import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  detailPendingUserSchema,
  validateUserDetailsSchema,
} from "../../schemas/CrmEnquiry.schemas";
import { CreateAgentText } from "./CreateAgentHelper";
import { appAxios } from "../../axios/axios";
import { useAppSelector } from "../../store";
import ReCaptcha from "../Common/Ui/ReCaptcha";
import { v4 as UUIDV4 } from "uuid";
import { LoadingButton } from "../Common/Ui/LoadingButton";
import { IntlTelInputWrapper } from "../Common/Ui/IntlTelInputWrapper";
export enum userTypeEnum {
  retail = "retail",
  travelAgent = "travelAgent",
  corporate = "corporate",
  staff = "staff",
}
export type TUserPendingFormValues = {
  userType: userTypeEnum;
  email?: string;
  firstName: string;
  lastName?: string;
  phoneCode: string;
  phone: string;
  companyName?: string;
  panNumber?: string;
  gst?: string;
};

const CreateNewAgent: React.FC<{
  isModalOpen: boolean;
  handleModalClose: () => void;
  url: string;
  customerData?: Partial<TUserPendingFormValues> | null;
  queryData?: any;
  changeQueryData?: () => void;
}> = (props) => {
  const { data } = useAppSelector((state) => state.siteData);
  const [captchaToken, setCaptchaToken] = useState<string | undefined>();
  const [requestLoading, setRequestLoading] = useState(false);
  const reCaptchaRef = useRef<any>();

  const {
    handleSubmit,
    register,
    getValues,
    setValue,
    formState,
    clearErrors,
    setError,
    reset,
    watch,
  } = useForm<TUserPendingFormValues>({
    resolver: joiResolver(detailPendingUserSchema),
    defaultValues: {
      companyName: "default value",
      firstName: "",
      lastName: "",
      email: "",
      gst: "",
      panNumber: "",
      phone: "",
      phoneCode: "",
    },
  });
  const errors = formState.errors;

  const onSubmit = async (userData: TUserPendingFormValues) => {
    try {
      // const { errorMessage } = validateUserDetailsSchema(userData);

      // if (errorMessage) {
      //   return toast.error(errorMessage);
      // }
      if (userData.email?.trim().length === 0) {
        userData.email = UUIDV4() + "@mailinator.com";
      }

      let token;
      if (!captchaToken) {
        token = await reCaptchaRef.current.executeAsync();
        setCaptchaToken(token);
      }
      if (!captchaToken && !token) {
        return toast.error("Please solve captcha");
      }
      setRequestLoading(true);

      // if query data is not available that means only create a user
      const dataToBeSend = props.queryData
        ? {
            userData: {
              customer: userData,
              userType: userTypeEnum.retail,
            },
            enquiryData: props.queryData,
          }
        : {
            userData: {
              customer: userData,
              userType: userTypeEnum.retail,
            },
          };

      const signupResponse = await appAxios(props.url, {
        method: "POST",
        data: dataToBeSend,
        headers: {
          "site-id": data._id,
          "captcha-token": captchaToken ? captchaToken : token,
        },
      });
      if (signupResponse.data.success) {
        toast.success("User Created Successfully");
        props.queryData && toast.success("Enquiry Saved Successfully");
        reset();
        props.handleModalClose();
        props.changeQueryData && props.changeQueryData();
      } else {
        throw signupResponse;
      }
    } catch (error: any) {
      const errorMessage =
        error?.data?.error?.errorMessage || "Unable to save enquiry!!";

      toast.error(error?.response?.data?.error?.errorMessage || errorMessage);
    } finally {
      setRequestLoading(false);
    }
    reCaptchaRef.current.reset();
    setCaptchaToken(undefined);
  };

  useEffect(() => {
    if (props.customerData) {
      reset(props.customerData);
    }
  }, [props.customerData]);

  return (
    <Modal show={props.isModalOpen} onHide={props.handleModalClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Create New User</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form>
          <div className="row">
            <Form.Group className="col-12"></Form.Group>
            <CreateAgentText
              label="First Name"
              labelClassName="required"
              register={register("firstName")}
              type="text"
              error={errors.firstName}
            />
            <CreateAgentText
              label="Last Name"
              register={register("lastName")}
              type="text"
              error={errors.lastName}
            />

            <CreateAgentText
              label="Email"
              register={register("email")}
              type="email"
              error={errors.email}
            />
            <Form.Group className="col-md-6">
              <IntlTelInputWrapper
                label="Phone"
                labelClassName="mb-2"
                fieldId="phone"
                value={watch("phone")}
                isRequiredField={true}
                onChange={(phoneNumber, phoneCode, isValid) => {
                  setValue("phone", phoneNumber);
                  setValue("phoneCode", phoneCode);
                  if (!isValid) {
                    setError("phone", {
                      type: "manual",
                      message: "Please enter a valid phone number",
                    });
                  } else {
                    clearErrors("phone");
                  }
                }}
                errorMessage={errors.phone?.message}
              />
            </Form.Group>
            <CreateAgentText
              label="Pan Number"
              register={register("panNumber")}
              type="text"
              error={errors.panNumber}
            />
            <CreateAgentText
              label="GST"
              register={register("gst")}
              type="text"
              error={errors.gst}
            />
            <ReCaptcha
              setToken={(token: string) => {
                setCaptchaToken(token);
              }}
              myRef={reCaptchaRef}
            />
          </div>
          <div className="mt-4 ">
            <div className="row ">
              <div className="col-12 d-flex justify-content-end">
                <LoadingButton
                  loading={requestLoading}
                  minWidth="93px"
                  onClick={handleSubmit(onSubmit)}
                  disabled={Object.keys(errors).length > 0}
                >
                  Create User
                </LoadingButton>
              </div>
            </div>
          </div>
        </form>
        <ReCaptcha
          setToken={(token: string) => {
            setCaptchaToken(token);
          }}
          myRef={reCaptchaRef}
        />
      </Modal.Body>
    </Modal>
  );
};

export default CreateNewAgent;
